import { Roles } from "common";
import env from "env";
import { useMemo } from "react";
import {
  useGetOrganisationRelationshipsQuery,
  useGetOrganizationQuery,
  useGetProfileQuery,
} from "redux/services/membership";
import { getOrganisationMembership } from "utils";
import { useTypedSelector } from "redux/hooks";
import { selectAuth } from "redux/slices/authSlice";

interface UseRolesResult {
  isPSPMakerCheckerRole: boolean;
  isLoading: boolean;
  isPSPMakerRole: boolean;
  isPSPCheckerRole: boolean;
  isPSPAdminRole: boolean;
  isPaymentOpsRole: boolean;
  isInHouseCheckerRole: boolean;
  isInhouseMakerRole: boolean;
  isInhouseApproverRole: boolean;
  isBUAdminPSP: boolean;
  isSharedServiceRole: boolean;
  isPSPApproverRole: boolean;
  isBUAdmin: boolean;
  displayRoleName: string;
}

export const useRoles = (): UseRolesResult => {
  const {
    data: profileData,
    isLoading: loadingProfile,
    error: errorProfile,
  } = useGetProfileQuery();

  // Get persisted data from Redux auth state
  const authState = useTypedSelector(selectAuth);
  const { organisationData, userRole } = authState;

  const membershipOrganisation = getOrganisationMembership(
    profileData?.data?.memberships
  );

  const { data: orgData } = useGetOrganizationQuery(
    {
      orgId: membershipOrganisation?.organisationId ?? "",
      userId: profileData?.data?.userId ?? "",
    },
    {
      skip:
        !membershipOrganisation?.organisationId || !profileData?.data?.userId,
      refetchOnMountOrArgChange: false,
    }
  );

  const shouldSkipOrgRel = useMemo(
    () =>
      !!(
        loadingProfile ||
        errorProfile ||
        !membershipOrganisation?.organisationId
      ),
    [loadingProfile, errorProfile, membershipOrganisation?.organisationId]
  );

  const { data: dataGetOrgRel, isFetching: isFetchingGetOrgRel } =
    useGetOrganisationRelationshipsQuery(
      {
        executorOrgId: membershipOrganisation?.organisationId!,
        process: env.api.approvalCategoryCode,
        role: Roles.BusinessOwner,
      },
      { skip: shouldSkipOrgRel }
    );

  const isPaymentOpsRole = useMemo(
    () =>
      (userRole &&
        userRole.indexOf(Roles.PaymentOps) !== -1) ??
      false,
    [userRole]
  );

  const { data: organizationData, isFetching: isFetchingGetOrg } =
    useGetOrganizationQuery(
      {
        orgId: membershipOrganisation?.organisationId ?? "",
        userId: profileData?.data?.userId ?? "",
      },
      {
        skip: !(profileData && membershipOrganisation && !isPaymentOpsRole),
        refetchOnMountOrArgChange: false,
      }
    );

  const isLoading = useMemo(
    () => loadingProfile || isFetchingGetOrgRel || isFetchingGetOrg,
    [loadingProfile, isFetchingGetOrgRel, isFetchingGetOrg]
  );

  const isPSPApproverRole = useMemo(() => {
    if (isLoading) return false;
    if (!userRole?.includes(Roles.BUApprover)) return false;
    const customeFields = organizationData?.data?.customFields;
    if (customeFields?.length === 0) return false;
    if (
      customeFields?.filter(
        (field: any) =>
          field.customKey === env.app.payrollManagedCustomKey &&
          field.customValue === env.app.payrollManagedPSP
      ).length === 0
    )
      return false;
    return true;
  }, [
    isLoading,
    organizationData?.data?.customFields,
    userRole,
  ]);

  const isBUAdminPSP = useMemo(
    () =>
      (!isPSPApproverRole &&
        organizationData?.data?.customFields?.some(
          (item: any) =>
            item.customKey === env.app.payrollManagedCustomKey &&
            item.customValue === env.app.payrollManagedPSP
        )) ??
      false,
    [isPSPApproverRole, organizationData?.data?.customFields]
  );

  const isPSPAdminRole = useMemo(
    () =>
      (organisationData?.organizationRole === Roles.PSP &&
        userRole?.includes(Roles.BusinessAdmin)) ??
      false,
    [organisationData?.organizationRole, userRole]
  );

  const isPSPMakerCheckerRole = useMemo(() => {
    if (isLoading) return false;

    if (
      ![Roles.BUChecker, Roles.BUMaker].some((role) =>
        userRole?.includes(role)
      )
    )
      return false;

    if (orgData?.data?.orgRole === Roles.PSP) return true;

    if (
      dataGetOrgRel?.data?.some(
        (item) => item.executorOrg.id === item.ownerOrg.id
      ) ||
      dataGetOrgRel?.data?.length === 0
    )
      return false;

    return true;
  }, [
    dataGetOrgRel?.data,
    isLoading,
    orgData?.data?.orgRole,
    profileData?.data?.listRoles,
  ]);

  const isPSPMakerRole = useMemo(() => {
    if (isLoading) return false;
    if (!userRole?.includes(Roles.BUMaker)) return false;

    if (orgData?.data?.orgRole === Roles.PSP) return true;

    if (
      dataGetOrgRel?.data?.some(
        (item) => item.executorOrg.id === item.ownerOrg.id
      ) ||
      dataGetOrgRel?.data?.length === 0
    )
      return false;

    return true;
  }, [
    dataGetOrgRel?.data,
    isLoading,
    orgData?.data?.orgRole,
    userRole,
  ]);

  const isPSPCheckerRole = useMemo(() => {
    if (isLoading) return false;

    if (!userRole?.includes(Roles.BUChecker)) return false;

    if (orgData?.data?.orgRole === Roles.PSP) return true;

    if (
      dataGetOrgRel?.data?.some(
        (item) => item.executorOrg.id === item.ownerOrg.id
      ) ||
      dataGetOrgRel?.data?.length === 0
    )
      return false;

    return true;
  }, [
    dataGetOrgRel?.data,
    isLoading,
    orgData?.data?.orgRole,
    userRole,
  ]);

  const isInHouseCheckerRole =
    (!isPSPCheckerRole &&
      userRole?.includes(Roles.BusinessApprover)) ??
    false;

  const isInhouseMakerRole =
    (!isPSPMakerRole &&
      userRole?.includes(Roles.BUMaker)) ??
    false;

  const isInhouseApproverRole =
    (!isPSPApproverRole &&
      userRole?.includes(Roles.BUApprover)) ??
    false;

  const isSharedServiceRole = useMemo(
    () =>
      organizationData?.data?.customFields?.some(
        (item: any) =>
          item.customKey === env.app.payrollManagedCustomKey &&
          item.customValue === env.app.payrollManagedSharedService
      ) ?? false,
    [organizationData]
  );

  const isBUAdmin = useMemo(
    () =>
      (!isPSPAdminRole &&
        !isBUAdminPSP &&
        userRole?.includes(Roles.BusinessAdmin)) ??
      false,
    [isBUAdminPSP, isPSPAdminRole, userRole]
  );

  const displayRoleName = useMemo(() => {
    if (isLoading) return "";
    if (isPaymentOpsRole) return "PaymentOps";
    if (isBUAdmin || isBUAdminPSP) return "Business Admin";
    if (isInHouseCheckerRole) return "Business Checker";
    if (isInhouseMakerRole) return "Business Maker";
    if (isInhouseApproverRole || isPSPApproverRole) return "Business Approver";
    if (isPSPAdminRole) return "PSP Admin";
    if (isPSPMakerRole) return "PSP Maker";
    if (isPSPCheckerRole) return "PSP Checker";
    return "Unknown Role";
  }, [
    isLoading,
    isPaymentOpsRole,
    isBUAdmin,
    isInHouseCheckerRole,
    isInhouseMakerRole,
    isInhouseApproverRole,
    isBUAdminPSP,
    isPSPAdminRole,
    isPSPMakerRole,
    isPSPCheckerRole,
    isPSPApproverRole,
  ]);

  return {
    isPaymentOpsRole,
    isPSPAdminRole,
    isPSPMakerCheckerRole,
    isLoading,
    isPSPMakerRole,
    isPSPCheckerRole,
    isInHouseCheckerRole,
    isInhouseMakerRole,
    isInhouseApproverRole,
    isBUAdminPSP,
    isSharedServiceRole,
    isPSPApproverRole,
    isBUAdmin,
    displayRoleName,
  };
};
